import { useState } from "react";
import type { ChangeEvent, SelectHTMLAttributes } from "react";
import NumberFormat, { type NumberFormatValues } from "react-number-format";

import { capitalize } from "../../../typography";

const PROPERTY_TYPES = [
  "single_family",
  "vacant_land",
  "retail",
  "multi_family",
  "agriculture",
  "office",
  "business",
  "other",
  "industrial",
  "parking",
  "recreational",
  "hospitality",
  "institutional_special_purpose",
] as const;

interface Filters {
  sort_by: string;
  number_of_bathrooms: string;
  number_of_bedrooms: string;
  property_types: (typeof PROPERTY_TYPES)[];
  price_min?: string;
  price_max?: string;
}

interface Props {
  handleFilterSubmit: (filters: Filters) => void;
}

const PropertyListingsSearchFilters = ({ handleFilterSubmit }: Props) => {
  const [filters, setFilters] = useState<Filters>({
    sort_by: "Relevance",
    number_of_bathrooms: "Any",
    number_of_bedrooms: "Any",
    property_types: [],
  });

  const handlePriceRangeChange = (rangeType: string, values: NumberFormatValues) => {
    setFilters({ ...filters, [rangeType]: values.value });

    handleFilterSubmit({
      ...filters,
      [rangeType]: values.value,
    });
  };

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, [e.target.id]: e.target.value });

    handleFilterSubmit({
      ...filters,
      [e.target.id]: e.target.value,
    });
  };

  const handlePropertyTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newProperty = e.target.value as any as typeof PROPERTY_TYPES;
    let newPropertyTypes: (typeof PROPERTY_TYPES)[] = [...filters.property_types, newProperty];

    if (!e.target.checked) {
      newPropertyTypes = filters.property_types.filter(propertyType => {
        return propertyType !== newProperty;
      });
    }

    setFilters({
      ...filters,
      property_types: newPropertyTypes,
    });

    handleFilterSubmit({
      ...filters,
      property_types: newPropertyTypes,
    });
  };

  const getNumberOfRoomsSelector = (
    id: string,
    value: string,
    onChange: SelectHTMLAttributes<HTMLSelectElement>["onChange"],
  ) => {
    return (
      <select id={id} value={value} onChange={onChange}>
        <option value="Any">Any</option>
        <option value="1">1</option>
        <option value="1+">1+</option>
        <option value="2">2</option>
        <option value="2+">2+</option>
        <option value="3">3</option>
        <option value="3+">3+</option>
        <option value="4">4</option>
        <option value="4+">4+</option>
        <option value="5">5</option>
        <option value="5+">5+</option>
      </select>
    );
  };

  const { sort_by, property_types, number_of_bathrooms, number_of_bedrooms } = filters;

  return (
    <div id="property-listings-search-filters-component" className="small-12 medium-3 columns">
      <div>
        <label className="red">Sort By</label>
        <select id="sort_by" value={sort_by} onChange={handleChange}>
          <option value="Highest Price">Higest Price</option>
          <option value="Lowest Price">Lowest Price</option>
          <option value="Relevance">Relevance</option>
        </select>
        <div className="property-type-filters-container">
          <label className="red">Property Type</label>
          {PROPERTY_TYPES.map((type, i) => {
            return (
              <div key={i} className="property-type-filters">
                <input
                  type="checkbox"
                  id={type}
                  name={type}
                  value={type}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  checked={property_types.includes(type as any as typeof PROPERTY_TYPES)}
                  onChange={handlePropertyTypeChange}
                />
                <label htmlFor={type}>{capitalize(type.split("_")).join(" ")}</label>
              </div>
            );
          })}
        </div>
        <label className="red">Number of Bathrooms</label>
        {getNumberOfRoomsSelector("number_of_bathrooms", number_of_bathrooms, handleChange)}
        <label className="red">Number of Bedrooms</label>
        {getNumberOfRoomsSelector("number_of_bedrooms", number_of_bedrooms, handleChange)}
        <label className="red">Price</label>
        <NumberFormat
          thousandSeparator={true}
          prefix={"$"}
          onValueChange={values => handlePriceRangeChange("price_min", values)}
          placeholder={"Minimum price"}
        />{" "}
        -
        <span>
          {" "}
          <NumberFormat
            thousandSeparator={true}
            prefix={"$"}
            onValueChange={values => handlePriceRangeChange("price_max", values)}
            placeholder={"Maximum price"}
          />
        </span>
      </div>
    </div>
  );
};

export { PropertyListingsSearchFilters };
export type { Filters };
